'use client';

import { useState } from 'react';
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLinkIcon } from 'lucide-react';

import CallScoreBadge from '@/components/call/call-score-badge';
import { DataTable } from '@/components/data-table';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { formatDurationFromMillis } from '@/lib/format';

import { CsrAggViewItem } from './board-csr-provider';

export function CSRTable({ data }: { data: CsrAggViewItem[] }) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns: COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return <DataTable table={table} columns={COLUMNS} />;
}

const COLUMNS: ColumnDef<CsrAggViewItem>[] = [
  {
    accessorKey: 'csr_name',
    header: ({ column }) => <SortableHeader label='CSR' column={column} />,
    cell: ({ row }) => <div className='px-2 font-medium'>{row.original.csr_name}</div>,
  },
  {
    accessorKey: 'booking_rate',
    header: ({ column }) => <SortableHeader label='Booking Rate' column={column} />,
    cell: ({ row }) => <div className='px-2 font-medium'>{row.original.booking_rate.toFixed(2)}%</div>,
  },
  {
    accessorKey: 'resolution_rate',
    header: ({ column }) => <SortableHeader label='Resolution Rate' column={column} />,
    cell: ({ row }) => (
      <div className='px-2 font-medium'>
        {row.original.resolution_rate ? row.original.resolution_rate.toFixed(2) + '%' : '-'}
      </div>
    ),
  },
  {
    accessorKey: 'rate_metrics_score',
    header: ({ column }) => <SortableHeader label='Pass Metrics Rate' column={column} />,
    cell: ({ row }) => (
      <div className='px-2 font-medium'>
        <div>{(row.original.rate_metrics_score || 0).toFixed(2)}%</div>
      </div>
    ),
  },
  {
    accessorKey: 'avg_metrics_score',
    header: ({ column }) => <SortableHeader label='Avg. Metrics Score' column={column} />,
    cell: ({ row }) => (
      <div className='px-2 font-medium'>
        <CallScoreBadge score={row.original.avg_metrics_score} />
      </div>
    ),
  },
  {
    accessorKey: 'avg_call_duration_ms',
    header: ({ column }) => <SortableHeader label='Avg. Call Duration' column={column} />,
    cell: ({ row }) => (
      <div className='px-2 font-medium'>{formatDurationFromMillis(row.original.avg_call_duration_ms || 0, 'min')}</div>
    ),
  },
  {
    accessorKey: 'total_calls',
    header: ({ column }) => <SortableHeader label='Total Calls' column={column} />,
    cell: ({ row }) => <div className='px-2 font-medium'>{row.original.total_calls}</div>,
  },
  {
    accessorKey: 'ring_groups',
    header: 'Ring Group',
    cell: ({ row }) => (
      <div className='max-w-[200px] px-2 font-medium'>
        <div className='truncate'>{(row.original.ring_groups || []).join(', ')}</div>
      </div>
    ),
  },
  {
    accessorKey: 'teams',
    header: 'Team',
  },
  {
    accessorKey: 'actions',
    header: () => '',
    cell: ({ row }) => (
      <div className='flex justify-end'>
        <Link href={`/analytics/call-analytics?return_to=dashboard&csr=${encodeURIComponent(row.original.csr_name)}`}>
          <Button size='icon' variant='outline'>
            <ExternalLinkIcon className='h-4 w-4' />
          </Button>
        </Link>
      </div>
    ),
  },
];

function SortableHeader({ label, column }: { label: string; column: Column<CsrAggViewItem> }) {
  const isSorted = column.getIsSorted();
  return (
    <Button
      variant='ghost'
      onClick={() => {
        if (isSorted === false) {
          column.toggleSorting(true);
        } else if (isSorted === 'desc') {
          column.toggleSorting(false);
        } else {
          column.clearSorting();
        }
      }}
    >
      {label}
      {isSorted === 'asc' ? (
        <ArrowUp className='ml-2 h-4 w-4' />
      ) : isSorted === 'desc' ? (
        <ArrowDown className='ml-2 h-4 w-4' />
      ) : (
        <ArrowUpDown className='ml-2 h-4 w-4' />
      )}
    </Button>
  );
}
